import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Mail, Phone, MapPin, Send, Check, Loader2, AlertCircle, Clock,
  Instagram, Facebook, ArrowUpRight, Navigation,
} from "lucide-react";
import Header from "../components/layout/Header";
import Footer from "../components/layout/Footer";
import SeoHead from "../components/SeoHead";
import { api } from "../lib/api";
import { CLINICS, SOCIAIS } from "../data/clinics";
import { CLINICAS_DETALHE, HORARIO_PADRAO } from "../data/clinicasDetalhe";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "../components/ui/accordion";

const ASSUNTOS = [
  "Pedido de informação",
  "Sugestão",
  "Reclamação",
  "Outro",
];

const FAQ = [
  {
    q: "Preciso de marcar consulta ou posso aparecer?",
    a: "Recomendamos marcação prévia para garantir atendimento atempado e dedicação ao seu animal. Pode marcar online (24/7), por telefone ou via WhatsApp. Em caso de urgência durante o horário de funcionamento, ligue-nos primeiro para que possamos preparar-nos.",
  },
  {
    q: "Têm urgências fora de horário?",
    a: "Durante o horário de funcionamento atendemos urgências mediante chamada prévia (ligue para a clínica mais próxima). Para situações fora do horário, contacte o telemóvel geral 965 650 700 — sempre que possível, encaminhamos ou damos orientação imediata; em casos críticos fora de horas, recomendamos o hospital veterinário regional.",
  },
  {
    q: "Fazem consultas ao domicílio?",
    a: "Em situações específicas (animais geriátricos, mobilidade reduzida ou ninhadas), avaliamos visitas ao domicílio mediante agendamento e disponibilidade da equipa. Contacte-nos com antecedência para confirmarmos data, hora e custo associado.",
  },
  {
    q: "Que métodos de pagamento aceitam?",
    a: "Aceitamos dinheiro, multibanco e MB Way. Para serviços de maior valor (cirurgias, internamento), é possível combinar pagamento parcelado mediante avaliação prévia — fale com a receção.",
  },
  {
    q: "Quando devo levar o meu cachorro ou gatinho à primeira consulta?",
    a: "Recomendamos a primeira consulta entre as 6 e 8 semanas de idade, para iniciar o protocolo vacinal, desparasitação interna/externa e avaliação geral. Animais adotados em idade adulta devem ser avaliados o mais cedo possível após a chegada a casa.",
  },
  {
    q: "Quais as vacinas obrigatórias para o meu animal?",
    a: "Em Portugal, a vacina antirrábica é obrigatória para cães a partir dos 4 meses (com reforço anual). Recomendamos também as vacinas polivalentes para cão e gato e outras conforme o estilo de vida do animal (passeios em coletivos, contacto com outros animais, viagens). Na consulta avaliamos o plano vacinal individualizado.",
  },
  {
    q: "Como faço para emitir o passaporte do meu animal?",
    a: "O passaporte é necessário para viagens internacionais. Para emissão, o seu animal precisa de microchip, vacina antirrábica em vigor e, dependendo do destino, testes serológicos. Marque uma consulta para avaliarmos o caso e organizarmos o processo dentro dos prazos exigidos pelo país de destino.",
  },
  {
    q: "Vendem alimentação e produtos especializados?",
    a: "Sim. As nossas clínicas dispõem de loja com alimentação seca e húmida (incluindo dietas veterinárias prescritas), suplementação, produtos de higiene, antiparasitários e acessórios. Trabalhamos com marcas como Royal Canin, Specific e outras.",
  },
];

function ClinicaContactCard({ slug }) {
  const c = CLINICS.find((x) => x.slug === slug);
  const det = CLINICAS_DETALHE[slug];
  const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${det.lat},${det.lng}`;
  const embed = `https://www.google.com/maps?q=${det.lat},${det.lng}&z=15&hl=pt-PT&output=embed`;
  return (
    <article className="rounded-3xl bg-white border border-[#1E5F6E]/10 overflow-hidden shadow-soft" data-testid={`clinic-contact-${slug}`}>
      <div className="p-6 md:p-7">
        <div className="text-xs uppercase tracking-widest font-bold text-[#88A096]">Clínica</div>
        <h3 className="mt-1 font-display text-xl md:text-2xl font-extrabold text-[#1A1A1A]">{c.nome}</h3>
        <ul className="mt-4 space-y-2.5 text-[14.5px]">
          <li className="flex items-start gap-2">
            <MapPin className="h-4 w-4 mt-0.5 text-[#88A096] shrink-0" />
            <a href={mapsUrl} target="_blank" rel="noopener noreferrer" className="text-[#1A1A1A] hover:text-[#1E5F6E] hover:underline underline-offset-4">
              {c.morada}, {c.codigoPostal} {c.cidade}
            </a>
          </li>
          <li className="flex items-center gap-2">
            <Phone className="h-4 w-4 text-[#88A096] shrink-0" />
            <a href={`tel:${c.telefoneE164}`} className="font-semibold text-[#1E5F6E] hover:underline underline-offset-4">
              {c.telefone}
            </a>
          </li>
          <li className="flex items-center gap-2">
            <Mail className="h-4 w-4 text-[#88A096] shrink-0" />
            <a href={`mailto:${det.email}`} className="text-[#1A1A1A] hover:text-[#1E5F6E] hover:underline underline-offset-4">
              {det.email}
            </a>
          </li>
        </ul>

        <div className="mt-5">
          <div className="flex items-center gap-2 mb-2">
            <Clock className="h-4 w-4 text-[#88A096]" />
            <span className="text-xs uppercase tracking-widest font-bold text-[#5A5A5A]">Horário</span>
          </div>
          <table className="w-full text-[13.5px]">
            <tbody>
              {HORARIO_PADRAO.map((d) => (
                <tr key={d.dia} className="border-t border-[#1E5F6E]/8">
                  <td className="py-1.5 text-[#1A1A1A]">{d.dia}</td>
                  <td className="py-1.5 text-right text-[#5A5A5A]">{d.aberto ? d.blocos.join(" • ") : "Fechado"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <iframe
        title={`Mapa - ${c.nome}`}
        src={embed}
        loading="lazy"
        referrerPolicy="no-referrer-when-downgrade"
        className="w-full h-[300px] border-0 border-t border-[#1E5F6E]/10"
      />
      <div className="flex flex-col sm:flex-row gap-2 p-5 bg-[#FAF7F2] border-t border-[#1E5F6E]/10">
        <Link
          to={`/marcar-consulta?clinica=${slug}`}
          data-testid={`cc-marcar-${slug}`}
          className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-[#1E5F6E] text-white px-4 py-2.5 text-sm font-bold hover:bg-[#174E5B]"
        >
          Marcar consulta
        </Link>
        <a
          href={mapsUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`cc-chegar-${slug}`}
          className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-white border-2 border-[#1E5F6E]/15 text-[#1E5F6E] px-4 py-2.5 text-sm font-bold hover:border-[#1E5F6E]/40"
        >
          <Navigation className="h-4 w-4" />
          Como chegar
        </a>
      </div>
    </article>
  );
}

function ContactForm() {
  const [form, setForm] = useState({
    nome: "", email: "", telefone: "", assunto: "Pedido de informação",
    mensagem: "", clinica_relacionada: "", aceita_politica: false, website: "",
  });
  const [errors, setErrors] = useState({});
  const [enviando, setEnviando] = useState(false);
  const [sucesso, setSucesso] = useState(false);
  const [erroSubmit, setErroSubmit] = useState(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const inputCls = "w-full rounded-xl bg-white border-2 border-[#1E5F6E]/10 px-4 py-3 text-sm focus:border-[#1E5F6E] focus:outline-none transition-colors";

  const validar = () => {
    const e = {};
    if (!form.nome || form.nome.trim().length < 2) e.nome = "Nome obrigatório.";
    if (!/^[^@]+@[^@]+\.[^@]+$/.test(form.email)) e.email = "Email inválido.";
    if (!form.assunto) e.assunto = "Selecione um assunto.";
    if (!form.clinica_relacionada) e.clinica_relacionada = "Selecione a clínica.";
    if (!form.mensagem || form.mensagem.trim().length < 5) e.mensagem = "Mensagem demasiado curta.";
    if (form.mensagem.length > 1000) e.mensagem = "Mensagem demasiado longa.";
    if (!form.aceita_politica) e.aceita_politica = "Tem de aceitar a política.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validar()) return;
    setEnviando(true);
    setErroSubmit(null);
    try {
      await api.post("/contactos", {
        ...form,
        // backend accepts only ribeira-brava/calheta; for "geral" we send null
        clinica_relacionada: ["ribeira-brava", "calheta"].includes(form.clinica_relacionada)
          ? form.clinica_relacionada
          : null,
      });
      setSucesso(true);
    } catch (err) {
      const status = err?.response?.status;
      let msg = err?.response?.data?.detail || "Não foi possível enviar. Tente novamente.";
      if (status === 429) msg = "Demasiados pedidos. Aguarde 1 hora e tente novamente.";
      setErroSubmit(typeof msg === "string" ? msg : "Erro inesperado.");
    } finally {
      setEnviando(false);
    }
  };

  if (sucesso) {
    return (
      <div className="text-center py-10" data-testid="contact-success">
        <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-emerald-100 text-emerald-700 mb-5">
          <Check className="h-7 w-7" strokeWidth={3} />
        </div>
        <h2 className="font-display text-2xl font-bold">Mensagem enviada.</h2>
        <p className="mt-2 text-[#5A5A5A]">Respondemos em 1-2 dias úteis.</p>
        <button
          onClick={() => {
            setSucesso(false);
            setForm({
              nome: "", email: "", telefone: "", assunto: "Pedido de informação",
              mensagem: "", clinica_relacionada: "", aceita_politica: false, website: "",
            });
          }}
          className="mt-6 inline-flex rounded-full bg-[#1E5F6E] text-white px-5 py-2.5 text-sm font-bold hover:bg-[#174E5B]"
        >
          Enviar outra mensagem
        </button>
      </div>
    );
  }

  const motivoCount = form.mensagem.length;

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
      <h2 className="font-display text-2xl md:text-3xl font-bold">Envie-nos uma mensagem</h2>
      <p className="text-sm text-[#5A5A5A] !mt-1">
        Para marcar consulta, use o nosso{" "}
        <Link to="/marcar-consulta" className="text-[#1E5F6E] font-bold underline underline-offset-4">
          sistema online
        </Link>{" "}
        ou ligue diretamente.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <label className="block">
          <span className="text-sm font-semibold">Nome completo <span className="text-rose-500">*</span></span>
          <input data-testid="c-nome" value={form.nome} onChange={(e) => set("nome", e.target.value)} className={inputCls + " mt-1"} />
          {errors.nome && <p className="text-xs text-rose-600 mt-1" data-testid="err-nome">{errors.nome}</p>}
        </label>
        <label className="block">
          <span className="text-sm font-semibold">Email <span className="text-rose-500">*</span></span>
          <input type="text" inputMode="email" autoComplete="email" data-testid="c-email" value={form.email} onChange={(e) => set("email", e.target.value)} className={inputCls + " mt-1"} />
          {errors.email && <p className="text-xs text-rose-600 mt-1" data-testid="err-email">{errors.email}</p>}
        </label>
        <label className="block">
          <span className="text-sm font-semibold">Telefone</span>
          <input data-testid="c-telefone" value={form.telefone} onChange={(e) => set("telefone", e.target.value)} className={inputCls + " mt-1"} />
        </label>
        <label className="block">
          <span className="text-sm font-semibold">Assunto <span className="text-rose-500">*</span></span>
          <select data-testid="c-assunto" value={form.assunto} onChange={(e) => set("assunto", e.target.value)} className={inputCls + " mt-1"}>
            {ASSUNTOS.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
        </label>
        <label className="block sm:col-span-2">
          <span className="text-sm font-semibold">Clínica relacionada <span className="text-rose-500">*</span></span>
          <select data-testid="c-clinica" value={form.clinica_relacionada} onChange={(e) => set("clinica_relacionada", e.target.value)} className={inputCls + " mt-1"}>
            <option value="">— Selecione —</option>
            <option value="ribeira-brava">Ribeira Brava</option>
            <option value="calheta">Calheta</option>
            <option value="geral">Geral (ambas)</option>
          </select>
          {errors.clinica_relacionada && <p className="text-xs text-rose-600 mt-1">{errors.clinica_relacionada}</p>}
        </label>
      </div>

      <label className="block">
        <span className="text-sm font-semibold">Mensagem <span className="text-rose-500">*</span></span>
        <textarea data-testid="c-mensagem" rows={5} maxLength={1000} value={form.mensagem} onChange={(e) => set("mensagem", e.target.value)} className={inputCls + " mt-1"} />
        <div className="mt-1 flex items-center justify-between">
          {errors.mensagem && <span className="text-xs text-rose-600">{errors.mensagem}</span>}
          <span className="ml-auto text-xs text-[#5A5A5A]">{motivoCount}/1000</span>
        </div>
      </label>

      {/* Honeypot */}
      <div className="absolute -left-[9999px] h-0 w-0 overflow-hidden" aria-hidden>
        <input value={form.website} onChange={(e) => set("website", e.target.value)} tabIndex="-1" />
      </div>

      <label className="flex items-start gap-3 cursor-pointer text-sm">
        <input type="checkbox" data-testid="c-aceita" checked={form.aceita_politica} onChange={(e) => set("aceita_politica", e.target.checked)} className="mt-1 h-4 w-4 accent-[#1E5F6E]" />
        <span>Li e aceito a <a href="/politica-de-privacidade" target="_blank" rel="noreferrer" className="text-[#1E5F6E] font-bold underline">política de privacidade</a>. <span className="text-rose-500">*</span></span>
      </label>
      {errors.aceita_politica && <p className="text-xs text-rose-600">{errors.aceita_politica}</p>}

      {erroSubmit && (
        <div className="rounded-xl bg-rose-50 border border-rose-200 text-rose-700 px-4 py-3 text-sm flex items-start gap-2" data-testid="contact-erro">
          <AlertCircle className="h-4 w-4 mt-0.5" /> {erroSubmit}
        </div>
      )}

      <button type="submit" disabled={enviando} data-testid="c-submit" className="inline-flex items-center gap-2 rounded-full bg-[#1E5F6E] text-white px-7 py-3.5 text-sm font-bold hover:bg-[#174E5B] disabled:opacity-40 transition-colors">
        {enviando ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        Enviar mensagem
      </button>
    </form>
  );
}

export default function ContactosPage() {
  const bothMapEmbed = `https://www.google.com/maps/d/u/0/embed?mid=&hl=pt-PT&ll=${(32.6747 + 32.7167) / 2},${(-17.0635 + -17.1769) / 2}&z=11`;

  return (
    <div className="min-h-screen bg-[#FAF7F2] text-[#1A1A1A]" data-testid="contactos-page">
      <SeoHead
        title="Contactos | Vet Clinic — Madeira"
        description="Fale connosco. Vet Clinic em Ribeira Brava e Calheta. Telefone, email, formulário e perguntas frequentes."
      />
      <Header />
      <main>
        {/* Hero */}
        <section className="relative overflow-hidden" data-testid="contactos-hero">
          <div
            className="absolute inset-0"
            style={{
              background:
                "radial-gradient(900px 380px at 85% -10%, rgba(30,95,110,0.10), transparent 60%), linear-gradient(180deg, #FAF7F2 0%, #EFE9DF 100%)",
            }}
          />
          <div className="container-app relative pt-16 pb-10 md:pt-24 md:pb-14">
            <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-[#88A096] mb-3">Contactos</span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-[60px] font-extrabold leading-[1.05] tracking-tight">
              Fale <span className="text-[#1E5F6E]">connosco</span>.
            </h1>
            <p className="mt-5 text-lg text-[#5A5A5A] max-w-2xl">
              Estamos cá para ajudar. Marque consulta, tire dúvidas ou deixe a sua sugestão.
            </p>
          </div>
        </section>

        {/* Clínicas detalhadas */}
        <section className="container-app py-12 md:py-16" data-testid="contactos-clinicas">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            <ClinicaContactCard slug="ribeira-brava" />
            <ClinicaContactCard slug="calheta" />
          </div>
        </section>

        {/* Formulário */}
        <section className="container-app py-12 md:py-16" data-testid="contactos-form">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-[#88A096] mb-3">
                Mensagem direta
              </span>
              <h2 className="font-display text-3xl md:text-4xl font-bold text-[#1A1A1A] tracking-tight">
                Prefere escrever?
              </h2>
              <p className="mt-3 text-[#5A5A5A] leading-relaxed">
                Preencha o formulário ao lado e a equipa da clínica entrará em contacto consigo.
              </p>
              <div className="mt-6 rounded-2xl bg-[#88A096]/10 border border-[#88A096]/30 p-4 text-sm text-[#1A1A1A]">
                <strong>Pretende marcar uma consulta?</strong>{" "}
                Use o nosso{" "}
                <Link to="/marcar-consulta" className="text-[#1E5F6E] font-bold underline underline-offset-4">
                  sistema online
                </Link>{" "}
                — é mais rápido.
              </div>
            </div>
            <div className="lg:col-span-7">
              <div className="rounded-3xl bg-white border border-[#1E5F6E]/10 p-7 md:p-9 shadow-soft relative">
                <ContactForm />
              </div>
            </div>
          </div>
        </section>

        {/* Encontre-nos online */}
        <section className="bg-white border-y border-[#1E5F6E]/8 py-14 md:py-20" data-testid="contactos-sociais">
          <div className="container-app">
            <div className="text-center max-w-2xl mx-auto mb-10">
              <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-[#88A096] mb-3">
                Encontre-nos online
              </span>
              <h2 className="font-display text-3xl md:text-4xl font-bold text-[#1A1A1A] tracking-tight">
                Acompanhe o nosso <span className="text-[#1E5F6E]">dia-a-dia</span>.
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-4xl mx-auto">
              <a
                href={SOCIAIS.instagram}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="social-instagram"
                className="group flex items-center justify-between gap-4 rounded-2xl bg-gradient-to-br from-[#f09433] via-[#dc2743] to-[#bc1888] text-white p-6 hover:-translate-y-1 transition-all shadow-soft"
              >
                <span className="flex items-center gap-3">
                  <Instagram className="h-6 w-6" />
                  <div className="text-left">
                    <div className="text-xs uppercase tracking-widest opacity-80">Instagram</div>
                    <div className="font-bold">@vetclinic.rbrava.calheta</div>
                  </div>
                </span>
                <ArrowUpRight className="h-5 w-5 group-hover:rotate-45 transition-transform" />
              </a>
              <a
                href={SOCIAIS.facebookRibeira}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="social-facebook-ribeira"
                className="group flex items-center justify-between gap-4 rounded-2xl bg-[#1877F2] text-white p-6 hover:-translate-y-1 transition-all shadow-soft"
              >
                <span className="flex items-center gap-3">
                  <Facebook className="h-6 w-6" />
                  <div className="text-left">
                    <div className="text-xs uppercase tracking-widest opacity-80">Facebook</div>
                    <div className="font-bold">Ribeira Brava</div>
                  </div>
                </span>
                <ArrowUpRight className="h-5 w-5 group-hover:rotate-45 transition-transform" />
              </a>
              <a
                href={SOCIAIS.facebookCalheta}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="social-facebook-calheta"
                className="group flex items-center justify-between gap-4 rounded-2xl bg-[#1877F2] text-white p-6 hover:-translate-y-1 transition-all shadow-soft"
              >
                <span className="flex items-center gap-3">
                  <Facebook className="h-6 w-6" />
                  <div className="text-left">
                    <div className="text-xs uppercase tracking-widest opacity-80">Facebook</div>
                    <div className="font-bold">Calheta</div>
                  </div>
                </span>
                <ArrowUpRight className="h-5 w-5 group-hover:rotate-45 transition-transform" />
              </a>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="container-app py-16 md:py-24" data-testid="contactos-faq">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-[#88A096] mb-3">
                Perguntas frequentes
              </span>
              <h2 className="font-display text-3xl md:text-4xl font-bold text-[#1A1A1A] tracking-tight">
                Respostas rápidas para as <span className="text-[#1E5F6E]">dúvidas</span> mais comuns.
              </h2>
              <p className="mt-4 text-[#5A5A5A] leading-relaxed">
                Não encontra o que procura? Use o formulário acima ou ligue diretamente.
              </p>
            </div>
            <div className="lg:col-span-7">
              <Accordion type="single" collapsible className="space-y-3" data-testid="faq-accordion">
                {FAQ.map((item, i) => (
                  <AccordionItem
                    key={i}
                    value={`item-${i}`}
                    className="rounded-2xl bg-white border border-[#1E5F6E]/10 px-5 data-[state=open]:border-[#1E5F6E]/30 data-[state=open]:shadow-soft transition-shadow"
                  >
                    <AccordionTrigger
                      data-testid={`faq-q-${i}`}
                      className="font-display text-left text-[15px] md:text-base font-bold text-[#1A1A1A] hover:no-underline"
                    >
                      {item.q}
                    </AccordionTrigger>
                    <AccordionContent
                      data-testid={`faq-a-${i}`}
                      className="text-[14.5px] text-[#5A5A5A] leading-relaxed pb-4"
                    >
                      {item.a.startsWith("{{") ? (
                        <span className="block rounded-xl bg-[#E8A87C]/10 border border-[#E8A87C]/30 px-3 py-2 text-[#7a3e1a]">
                          {item.a}
                        </span>
                      ) : item.a}
                      {item.nota && (
                        <span className="mt-2 block text-[12px] uppercase tracking-wider font-bold text-[#E8A87C]">
                          {item.nota}
                        </span>
                      )}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </section>

        {/* Localizações (mapa grande) */}
        <section className="container-app pb-20" data-testid="contactos-mapa-geral">
          <div className="max-w-2xl mb-8">
            <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-[#88A096] mb-3">
              Localizações
            </span>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-[#1A1A1A] tracking-tight">
              Estamos sempre perto de <span className="text-[#1E5F6E]">si</span>.
            </h2>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
            <div className="lg:col-span-8 rounded-3xl overflow-hidden border border-[#1E5F6E]/10 shadow-soft">
              <iframe
                title="Mapa Madeira - Vet Clinic"
                src={bothMapEmbed}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="w-full h-[450px] border-0"
              />
            </div>
            <div className="lg:col-span-4 space-y-4">
              {CLINICS.map((c) => {
                const det = CLINICAS_DETALHE[c.slug];
                const u = `https://www.google.com/maps/search/?api=1&query=${det.lat},${det.lng}`;
                return (
                  <div key={c.slug} className="rounded-2xl bg-white border border-[#1E5F6E]/10 p-5" data-testid={`map-legend-${c.slug}`}>
                    <div className="flex items-start gap-3">
                      <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-[#1E5F6E] text-white">
                        <MapPin className="h-5 w-5" />
                      </span>
                      <div>
                        <div className="text-xs uppercase tracking-widest font-bold text-[#88A096]">
                          {c.cidade}
                        </div>
                        <div className="font-display text-lg font-bold text-[#1A1A1A]">{c.nome}</div>
                        <p className="text-sm text-[#5A5A5A] mt-1">{c.morada}</p>
                        <a
                          href={u}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-2 inline-flex items-center gap-1 text-sm font-bold text-[#1E5F6E] hover:gap-2 transition-all"
                        >
                          Ver no mapa
                          <ArrowUpRight className="h-3.5 w-3.5" />
                        </a>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
